import { useTheme } from '@mui/material';
import React, { useMemo } from 'react';

import { Chart } from './chart';
import { BODY_GLYPHS, norm360, SIGN_GLYPHS } from './zodiac';

const SIZE = 400;
const C = SIZE / 2;
const R_OUTER = 192;
const R_SIGNS = 164;
const R_PLANETS = 138;
const R_INNER = 108;
/** Degrees kept between neighbouring planet glyphs. */
const MIN_GAP = 9;

interface Props {
    chart: Chart;
    label: string;
}

/** Spreads glyphs that sit close together so they do not overlap; returns display longitudes by id. */
export const declutter = (
    items: { id: string; lon: number }[]
): Map<string, number> => {
    const sorted = [...items].sort((a, b) => a.lon - b.lon);
    const shown = sorted.map((i) => i.lon);
    if (sorted.length > 1) {
        for (let pass = 0; pass < 60; pass++) {
            let moved = false;
            for (let i = 0; i < sorted.length; i++) {
                const j = (i + 1) % sorted.length;
                const gap = norm360(shown[j] - shown[i]);
                if (gap < MIN_GAP) {
                    const push = (MIN_GAP - gap) / 2;
                    shown[i] -= push;
                    shown[j] += push;
                    moved = true;
                }
            }
            if (!moved) break;
        }
    }
    return new Map(sorted.map((it, i) => [it.id, norm360(shown[i])]));
};

type Pt = { x: number; y: number };
const segment = (a: Pt, b: Pt) =>
    ({ x1: a.x, y1: a.y, x2: b.x, y2: b.y }) as const;

/**
 * The chart as a wheel: signs on the rim, houses, planets and lines between aspecting planets. With a
 * birth time the ascendant sits at nine o'clock; without one, 0° Aries does.
 */
export const ChartWheel: React.FC<Props> = ({ chart, label }) => {
    const theme = useTheme();
    const gold = theme.palette.primary.main;
    const soft = theme.palette.text.secondary;
    const ink = theme.palette.text.primary;

    const { at, display } = useMemo(() => {
        const origin = chart.angles ? chart.angles.asc : 0;
        const place = (lon: number, r: number): Pt => {
            const a = ((180 + norm360(lon - origin)) * Math.PI) / 180;
            return { x: C + r * Math.cos(a), y: C - r * Math.sin(a) };
        };
        return {
            at: place,
            display: declutter(
                chart.bodies.map((b) => ({ id: b.id, lon: b.lon }))
            ),
        };
    }, [chart]);

    const angleLabels: [string, number][] = chart.angles
        ? [
              ['AC', chart.angles.asc],
              ['MC', chart.angles.mc],
              ['DC', chart.angles.desc],
              ['IC', chart.angles.ic],
          ]
        : [];

    return (
        <svg
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            role="img"
            aria-label={label}
            style={{ width: '100%', maxWidth: 460, height: 'auto' }}
        >
            <circle
                cx={C}
                cy={C}
                r={R_OUTER}
                fill="rgba(21,14,36,.7)"
                stroke={gold}
            />
            <circle
                cx={C}
                cy={C}
                r={R_SIGNS}
                fill="none"
                stroke={gold}
                strokeOpacity={0.6}
            />
            <circle
                cx={C}
                cy={C}
                r={R_INNER}
                fill="none"
                stroke={gold}
                strokeOpacity={0.6}
            />

            {SIGN_GLYPHS.map((glyph, i) => {
                const mid = at(i * 30 + 15, (R_SIGNS + R_OUTER) / 2);
                return (
                    <g key={glyph}>
                        <line
                            {...segment(
                                at(i * 30, R_SIGNS),
                                at(i * 30, R_OUTER)
                            )}
                            stroke={gold}
                            strokeOpacity={0.6}
                        />
                        <text
                            x={mid.x}
                            y={mid.y}
                            fill={gold}
                            fontSize={17}
                            textAnchor="middle"
                            dominantBaseline="central"
                        >
                            {glyph}
                        </text>
                    </g>
                );
            })}

            {chart.houses?.map((cusp, i, cusps) => {
                const angular = i % 3 === 0;
                const next = cusps[(i + 1) % 12];
                const number = at(
                    cusp + norm360(next - cusp) / 2,
                    R_INNER - 12
                );
                return (
                    <g key={i}>
                        <line
                            {...segment(at(cusp, R_INNER), at(cusp, R_SIGNS))}
                            stroke={gold}
                            strokeWidth={angular ? 1.8 : 0.7}
                            strokeOpacity={angular ? 1 : 0.55}
                        />
                        <text
                            x={number.x}
                            y={number.y}
                            fill={soft}
                            fontSize={9}
                            textAnchor="middle"
                            dominantBaseline="central"
                        >
                            {i + 1}
                        </text>
                    </g>
                );
            })}

            {chart.aspects
                .filter(
                    (a) =>
                        a.type !== 'conjunction' &&
                        a.a !== 'asc' &&
                        a.a !== 'mc' &&
                        a.b !== 'asc' &&
                        a.b !== 'mc'
                )
                .map((a) => {
                    const pa = chart.bodies.find((b) => b.id === a.a);
                    const pb = chart.bodies.find((b) => b.id === a.b);
                    if (!pa || !pb) return null;
                    const hard = a.type === 'square' || a.type === 'opposition';
                    return (
                        <line
                            key={`${a.a}-${a.b}`}
                            {...segment(
                                at(pa.lon, R_INNER - 2),
                                at(pb.lon, R_INNER - 2)
                            )}
                            stroke={hard ? theme.palette.secondary.main : gold}
                            strokeOpacity={0.5}
                            strokeWidth={0.8}
                            strokeDasharray={
                                a.type === 'sextile' ? '3 3' : undefined
                            }
                        />
                    );
                })}

            {chart.bodies.map((b) => {
                const glyph = at(display.get(b.id) ?? b.lon, R_PLANETS);
                return (
                    <g key={b.id}>
                        <line
                            {...segment(
                                at(b.lon, R_SIGNS),
                                at(b.lon, R_SIGNS - 7)
                            )}
                            stroke={ink}
                            strokeOpacity={0.8}
                        />
                        <text
                            x={glyph.x}
                            y={glyph.y}
                            fill={ink}
                            fontSize={18}
                            textAnchor="middle"
                            dominantBaseline="central"
                        >
                            {BODY_GLYPHS[b.id]}
                        </text>
                        {b.retrograde && (
                            <text
                                x={glyph.x + 9}
                                y={glyph.y + 8}
                                fill={soft}
                                fontSize={8}
                            >
                                R
                            </text>
                        )}
                    </g>
                );
            })}

            {angleLabels.map(([name, lon]) => {
                const p = at(lon, R_INNER - 24);
                return (
                    <text
                        key={name}
                        x={p.x}
                        y={p.y}
                        fill={gold}
                        fontSize={9}
                        fontWeight={700}
                        textAnchor="middle"
                        dominantBaseline="central"
                    >
                        {name}
                    </text>
                );
            })}
        </svg>
    );
};
